/**
 * The assistant's out-of-band metadata channel.
 *
 * Every assistant reply ends with a single trailing line:
 *   <<META {"suggestions": ["...", "..."], "gathered": ["business", ...]}>>
 *
 * The client strips this off before displaying the message, then parses it to
 * drive the suggested-reply chips and the "what we've gathered" progress. Doing
 * it as a text trailer (rather than a tool call) keeps the conversation a plain
 * stream of text turns and survives token-by-token streaming — the UI just
 * renders the text *before* the sentinel, which never appears mid-stream.
 *
 * Pure module (no React / no server-only imports) so both the system-prompt
 * builder and the client hook can share the exact contract.
 */

export const META_PREFIX = "<<META";

/** The brief sections the assistant can report as gathered, in interview order. */
export const GATHERED_STEPS = [
  "business",
  "goal",
  "package",
  "care",
  "timeline",
  "contact",
] as const;

export type GatheredStep = (typeof GATHERED_STEPS)[number];

export interface TurnMeta {
  readonly suggestions: string[];
  readonly gathered: GatheredStep[];
}

const EMPTY: TurnMeta = { suggestions: [], gathered: [] };

/** The visible portion of an assistant message (everything before the meta).
 *  Also trims a *partial* trailing sentinel so it never flashes mid-stream. */
export function stripMeta(text: string): string {
  const i = text.indexOf(META_PREFIX);
  if (i !== -1) return text.slice(0, i).trimEnd();
  for (let n = Math.min(META_PREFIX.length - 1, text.length); n > 0; n--) {
    if (text.endsWith(META_PREFIX.slice(0, n))) {
      return text.slice(0, text.length - n).trimEnd();
    }
  }
  return text;
}

/** Parse the trailing meta line; returns empty values when absent or malformed. */
export function parseMeta(text: string): TurnMeta {
  const at = text.indexOf(META_PREFIX);
  if (at === -1) return EMPTY;
  const seg = text.slice(at + META_PREFIX.length);
  const start = seg.indexOf("{");
  const end = seg.lastIndexOf("}");
  if (start === -1 || end <= start) return EMPTY;
  try {
    const obj = JSON.parse(seg.slice(start, end + 1)) as {
      suggestions?: unknown;
      gathered?: unknown;
    };
    const suggestions = Array.isArray(obj.suggestions)
      ? obj.suggestions.filter((s): s is string => typeof s === "string").slice(0, 4)
      : [];
    const gathered = Array.isArray(obj.gathered)
      ? obj.gathered.filter((s): s is GatheredStep =>
          (GATHERED_STEPS as readonly string[]).includes(s),
        )
      : [];
    return { suggestions, gathered };
  } catch {
    return EMPTY;
  }
}
